import { watch, onUnmounted, getCurrentInstance, type Ref } from 'vue'

export interface ModalStackEntry {
  id: symbol
  isOpenRef?: Ref<boolean>
  close: () => void
  skipHistoryBack?: boolean
  onRequestClose?: () => void | Promise<void>
  isInline?: boolean
}

const activeModalStack: ModalStackEntry[] = []
let globalPopStateAttached = false
let globalEscapeAttached = false
let isPopStateClosing = false
let isProgrammaticBack = false
let pushedModalHistoryCount = 0

function handleGlobalPopState(event?: PopStateEvent) {
  if (isProgrammaticBack) {
    isProgrammaticBack = false
    event?.stopImmediatePropagation?.()
    return
  }

  if (activeModalStack.length > 0) {
    // Intercept Back button during capture phase: close top modal and prevent Vue Router from navigating away
    event?.stopImmediatePropagation?.()
    isPopStateClosing = true
    try {
      const top = activeModalStack[activeModalStack.length - 1]
      if (top) {
        if (top.onRequestClose) {
          top.onRequestClose()
          return
        }
        activeModalStack.pop()
        top.close()
      }
      // Clean up any remaining entries that closed simultaneously
      while (
        activeModalStack.length > 0 &&
        activeModalStack[activeModalStack.length - 1].isOpenRef &&
        !activeModalStack[activeModalStack.length - 1].isOpenRef!.value
      ) {
        activeModalStack.pop()
      }
    } finally {
      isPopStateClosing = false
      pushedModalHistoryCount = Math.max(0, pushedModalHistoryCount - 1)
      if (activeModalStack.length === 0) {
        pushedModalHistoryCount = 0
        if (typeof window !== 'undefined' && window.history.state?.modalActive) {
          const currentVueState = window.history.state || {}
          window.history.replaceState({ ...currentVueState, modalActive: false }, '')
        }
      }
    }
  }
}

export function handleGlobalEscape(event?: KeyboardEvent): boolean {
  if (event && event.key !== 'Escape') return false

  const closed = closeTopModal(true)
  if (closed && event) {
    event.preventDefault()
    event.stopPropagation()
  }
  return closed
}

function onWindowEscapeKeyDown(e: KeyboardEvent) {
  if (e.defaultPrevented) return
  if (e.key === 'Escape') {
    if (e.repeat) {
      e.preventDefault()
      return
    }
    handleGlobalEscape(e)
  }
}

function attachGlobalListeners() {
  if (typeof window !== 'undefined') {
    if (!globalPopStateAttached) {
      // Register in capturing phase (true) so it intercepts Back navigation BEFORE Vue Router
      window.addEventListener('popstate', handleGlobalPopState, true)
      globalPopStateAttached = true
    }
    if (!globalEscapeAttached) {
      window.addEventListener('keydown', onWindowEscapeKeyDown, false)
      globalEscapeAttached = true
    }
  }
}

// Attach immediately if in browser environment
if (typeof window !== 'undefined') {
  attachGlobalListeners()
}

export function hasActiveModal(includeInline = false): boolean {
  for (let i = activeModalStack.length - 1; i >= 0; i--) {
    const item = activeModalStack[i]
    if (item.isOpenRef && !item.isOpenRef.value) {
      activeModalStack.splice(i, 1)
    }
  }
  if (includeInline) {
    return activeModalStack.length > 0
  }
  return activeModalStack.some(m => !m.isInline)
}

export function closeTopModal(triggerRequestClose = false): boolean {
  for (let i = activeModalStack.length - 1; i >= 0; i--) {
    const item = activeModalStack[i]
    if (item.isOpenRef && !item.isOpenRef.value) {
      activeModalStack.splice(i, 1)
    }
  }

  if (activeModalStack.length > 0) {
    const top = activeModalStack[activeModalStack.length - 1]
    if (top) {
      if (triggerRequestClose && top.onRequestClose) {
        top.onRequestClose()
        return true
      }
      isPopStateClosing = true
      try {
        activeModalStack.pop()
        top.close()
        while (
          activeModalStack.length > 0 &&
          activeModalStack[activeModalStack.length - 1].isOpenRef &&
          !activeModalStack[activeModalStack.length - 1].isOpenRef!.value
        ) {
          activeModalStack.pop()
        }
        return true
      } finally {
        isPopStateClosing = false
        if (!top.isInline) {
          pushedModalHistoryCount = Math.max(0, pushedModalHistoryCount - 1)
        }
        if (activeModalStack.length === 0) {
          pushedModalHistoryCount = 0
          if (typeof window !== 'undefined' && window.history.state?.modalActive) {
            const currentVueState = window.history.state || {}
            window.history.replaceState({ ...currentVueState, modalActive: false }, '')
          }
        }
      }
    }
  }
  return false
}

export type ModalCloseOptions = (() => void) | {
  onClose?: () => void
  onRequestClose?: () => void | Promise<void>
  isInline?: boolean
}

export function useModalBackHandler(isOpen: Ref<boolean>, onCloseOrOptions?: ModalCloseOptions) {
  const modalId = Symbol()

  const onClose = typeof onCloseOrOptions === 'function' ? onCloseOrOptions : onCloseOrOptions?.onClose
  const onRequestClose = typeof onCloseOrOptions === 'object' ? onCloseOrOptions?.onRequestClose : undefined
  const isInline = typeof onCloseOrOptions === 'object' ? !!onCloseOrOptions?.isInline : false

  attachGlobalListeners()

  watch(isOpen, (val) => {
    if (val) {
      document.body.classList.remove('is-dragging-active')
      // Add modal to active stack if not already present
      if (!activeModalStack.some(m => m.id === modalId || (m.isOpenRef && m.isOpenRef === isOpen))) {
        activeModalStack.push({
          id: modalId,
          isOpenRef: isOpen,
          close: () => {
            if (onClose) {
              onClose()
            } else {
              isOpen.value = false
            }
          },
          onRequestClose,
          isInline
        })

        // Push history state entry preserving Vue Router's internal history state (skip for inline edit modes)
        if (!isInline) {
          const currentVueState = (typeof window !== 'undefined' && window.history.state) || {}
          window.history.pushState({ ...currentVueState, modalActive: true, modalDepth: activeModalStack.length }, '')
          pushedModalHistoryCount++
        }
      }
    } else {
      // Remove modal from active stack
      const idx = activeModalStack.findIndex(m => m.id === modalId || (m.isOpenRef && m.isOpenRef === isOpen))
      if (idx !== -1) {
        const wasSkipped = !!activeModalStack[idx].skipHistoryBack
        const wasInline = !!activeModalStack[idx].isInline
        activeModalStack.splice(idx, 1)

        // If closed via UI (not via browser Back / popstate) and modal history state is active:
        // pop the modal history entry so the user does NOT have to press Back twice!
        if (!wasInline && !wasSkipped && !isPopStateClosing && pushedModalHistoryCount > 0) {
          pushedModalHistoryCount = Math.max(0, pushedModalHistoryCount - 1)
          isProgrammaticBack = true
          if (typeof window !== 'undefined' && window.history.state?.modalActive && activeModalStack.length === 0) {
            const currentVueState = window.history.state || {}
            window.history.replaceState({ ...currentVueState, modalActive: false }, '')
          }
          window.history.back()
        }
      }
    }
  }, { immediate: true, flush: 'sync' })

  if (getCurrentInstance()) {
    onUnmounted(() => {
      const idx = activeModalStack.findIndex(m => m.id === modalId || (m.isOpenRef && m.isOpenRef === isOpen))
      if (idx !== -1) {
        const wasSkipped = !!activeModalStack[idx].skipHistoryBack
        const wasInline = !!activeModalStack[idx].isInline
        activeModalStack.splice(idx, 1)
        if (!wasInline && !wasSkipped && !isPopStateClosing && pushedModalHistoryCount > 0) {
          pushedModalHistoryCount = Math.max(0, pushedModalHistoryCount - 1)
          isProgrammaticBack = true
          if (typeof window !== 'undefined' && window.history.state?.modalActive && activeModalStack.length === 0) {
            const currentVueState = window.history.state || {}
            window.history.replaceState({ ...currentVueState, modalActive: false }, '')
          }
          window.history.back()
        }
      }
      if (activeModalStack.length === 0) {
        pushedModalHistoryCount = 0
        if (typeof window !== 'undefined' && window.history.state?.modalActive) {
          const currentVueState = window.history.state || {}
          window.history.replaceState({ ...currentVueState, modalActive: false }, '')
        }
      }
    })
  }
}

export function dismissModalWithoutBack(isOpen: Ref<boolean>) {
  const entry = activeModalStack.find(m => m.isOpenRef === isOpen)
  if (entry) {
    entry.skipHistoryBack = true
  }
  if (!entry?.isInline) {
    pushedModalHistoryCount = Math.max(0, pushedModalHistoryCount - 1)
  }
  if (typeof window !== 'undefined' && window.history.state?.modalActive && activeModalStack.length <= 1) {
    const currentVueState = window.history.state || {}
    window.history.replaceState({ ...currentVueState, modalActive: false }, '')
  }
  isOpen.value = false
}

export function isModalHistoryBackPending(): boolean {
  return isProgrammaticBack
}

export function waitForModalHistoryBack(timeoutMs = 150): Promise<void> {
  if (!isProgrammaticBack) return Promise.resolve()
  return new Promise((resolve) => {
    let resolved = false
    const finish = () => {
      if (!resolved) {
        resolved = true
        resolve()
      }
    }
    const check = () => {
      if (!isProgrammaticBack) {
        finish()
      } else {
        setTimeout(check, 10)
      }
    }
    setTimeout(finish, timeoutMs)
    check()
  })
}

export function resetModalBackHandlerOnRouteNavigation() {
  activeModalStack.length = 0
  pushedModalHistoryCount = 0
  isPopStateClosing = false
  isProgrammaticBack = false
  if (typeof window !== 'undefined' && window.history.state?.modalActive) {
    const currentVueState = window.history.state || {}
    window.history.replaceState({ ...currentVueState, modalActive: false }, '')
  }
}


export function _resetModalBackHandlerForTesting() {
  activeModalStack.length = 0
  pushedModalHistoryCount = 0
  isPopStateClosing = false
  isProgrammaticBack = false
}

/**
 * Handles safe backdrop dismissals.
 * Prevents accidental modal closing when a user selects/drags text inside an input field
 * and releases the mouse button on the backdrop overlay.
 */
export function useBackdropDismiss(onDismiss: () => void) {
  let isBackdropDown = false

  const onBackdropMouseDown = (e: MouseEvent) => {
    isBackdropDown = (e.target === e.currentTarget)
  }

  const onBackdropMouseUp = (e: MouseEvent) => {
    const wasDown = isBackdropDown
    isBackdropDown = false
    if (wasDown && e.target === e.currentTarget) {
      onDismiss()
    }
  }

  const onContentMouseDown = (e: MouseEvent) => {
    e.stopPropagation()
    isBackdropDown = false
  }

  const onContentMouseUp = (e: MouseEvent) => {
    e.stopPropagation()
    isBackdropDown = false
  }

  const handleBackdropClick = (e: MouseEvent, phase: 'down' | 'up') => {
    if (phase === 'down') {
      onBackdropMouseDown(e)
    } else {
      onBackdropMouseUp(e)
    }
  }

  return {
    onBackdropMouseDown,
    onBackdropMouseUp,
    onContentMouseDown,
    onContentMouseUp,
    handleBackdropClick
  }
}

